import { useState, useEffect, useCallback } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Wand2, Copy, Link as LinkIcon } from "lucide-react";

export default function CompanionInvitesAdmin({ password }) {
  const [data, setData] = useState({ invites: [], companions: [] });
  const [creating, setCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [form, setForm] = useState({
    companion_id: "",
    style: "admirer",
    message: "",
    target_note: "",
    offer_amount: "",
    proposed_date: "",
    proposed_time: "",
    location: "",
    date_type: "",
  });

  const load = useCallback(() => {
    api.get(`/admin/companion-invites?admin_password=${password}`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [password]);
  useEffect(load, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch { /* noop */ }
    toast.success("Invite link copied!");
  };

  const create = async () => {
    if (!form.companion_id) {
      toast.error("Pick an AI companion");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        companion_id: form.companion_id,
        style: form.style,
        message: form.message || null,
        target_note: form.target_note || null,
        offer_amount: form.style === "dream_date" && form.offer_amount ? parseFloat(form.offer_amount) : null,
        proposed_date: form.style === "dream_date" ? form.proposed_date || null : null,
        proposed_time: form.style === "dream_date" ? form.proposed_time || null : null,
        location: form.style === "dream_date" ? form.location || null : null,
        date_type: form.style === "dream_date" ? form.date_type || null : null,
      };
      const r = await api.post(`/admin/companion-invites?admin_password=${password}`, payload);
      setCreatedLink(r.data.invite_link);
      copy(r.data.invite_link);
      setForm((p) => ({ ...p, message: "", target_note: "", offer_amount: "", proposed_date: "", proposed_time: "", location: "", date_type: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create invite");
    } finally {
      setCreating(false);
    }
  };

  const statusChip = (inv) => {
    if (inv.status === "claimed") {
      return <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/15 text-green-400 border border-green-500/30">✓ Claimed{inv.claimed_by_name ? ` by ${inv.claimed_by_name}` : ""}</span>;
    }
    if (inv.status === "opened") {
      return <span className="px-2 py-0.5 rounded-full text-xs bg-sky-500/15 text-sky-400 border border-sky-500/30">👀 Opened</span>;
    }
    return <span className="px-2 py-0.5 rounded-full text-xs bg-white/5 text-muted-foreground border border-white/10">Created</span>;
  };

  const selectCls = "flex h-10 w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div className="glass-card p-6 mb-6 border border-fuchsia-500/30" data-testid="companion-invites-section">
      <div className="flex items-center gap-3 mb-1">
        <Wand2 className="w-6 h-6 text-fuchsia-400" />
        <h2 className="text-lg font-semibold">AI Companion Mystery Invites</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Create a teaser link and share it on social media or contact forms — the recipient must sign up to reveal their surprise from the companion.
      </p>

      {data.summary && data.summary.total > 0 && (
        <div className="grid grid-cols-4 gap-3 mb-4" data-testid="invite-performance-summary">
          <div className="p-3 rounded-lg bg-white/5 text-center">
            <p className="text-xl font-bold" data-testid="invite-perf-total">{data.summary.total}</p>
            <p className="text-xs text-muted-foreground">Invites created</p>
          </div>
          <div className="p-3 rounded-lg bg-white/5 text-center">
            <p className="text-xl font-bold text-sky-400" data-testid="invite-perf-opened">{data.summary.opened}</p>
            <p className="text-xs text-muted-foreground">Opened ({data.summary.open_rate}%)</p>
          </div>
          <div className="p-3 rounded-lg bg-white/5 text-center">
            <p className="text-xl font-bold text-green-400" data-testid="invite-perf-claimed">{data.summary.claimed}</p>
            <p className="text-xs text-muted-foreground">Signed up ({data.summary.signup_rate}%)</p>
          </div>
          <div className="p-3 rounded-lg bg-white/5 text-center">
            <p className="text-xl font-bold text-amber-400" data-testid="invite-perf-paying">{data.summary.paying}</p>
            <p className="text-xs text-muted-foreground">Paying ({data.summary.paying_rate}%)</p>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">AI Companion</label>
          <select className={selectCls} value={form.companion_id} onChange={(e) => set("companion_id", e.target.value)} data-testid="invite-companion-select">
            <option value="">Select companion...</option>
            {data.companions.map((c) => (
              <option key={c.user_id} value={c.user_id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Invite style</label>
          <select className={selectCls} value={form.style} onChange={(e) => set("style", e.target.value)} data-testid="invite-style-select">
            <option value="admirer">💌 Secret admirer message</option>
            <option value="dream_date">🌹 Dream date invitation</option>
          </select>
        </div>
      </div>

      {form.style === "dream_date" && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3" data-testid="invite-dream-date-fields">
          <Input placeholder="Amount $" type="number" value={form.offer_amount} onChange={(e) => set("offer_amount", e.target.value)} className="bg-white/5 border-white/10" data-testid="invite-amount-input" />
          <Input placeholder="Date (YYYY-MM-DD)" value={form.proposed_date} onChange={(e) => set("proposed_date", e.target.value)} className="bg-white/5 border-white/10" data-testid="invite-date-input" />
          <Input placeholder="Time (19:00)" value={form.proposed_time} onChange={(e) => set("proposed_time", e.target.value)} className="bg-white/5 border-white/10" data-testid="invite-time-input" />
          <Input placeholder="Location" value={form.location} onChange={(e) => set("location", e.target.value)} className="bg-white/5 border-white/10" data-testid="invite-location-input" />
          <Input placeholder="Date type (Dinner...)" value={form.date_type} onChange={(e) => set("date_type", e.target.value)} className="bg-white/5 border-white/10" data-testid="invite-datetype-input" />
        </div>
      )}

      <Textarea
        placeholder={form.style === "dream_date" ? "Optional personal note included in the reveal..." : "Optional custom admirer message (leave blank for the companion's own flirty reveal)..."}
        value={form.message}
        onChange={(e) => set("message", e.target.value)}
        rows={2}
        className="bg-white/5 border-white/10 mb-3"
        data-testid="invite-message-input"
      />
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <Input
          placeholder="Target note for your records (e.g. IG @username) — never shown to the recipient"
          value={form.target_note}
          onChange={(e) => set("target_note", e.target.value)}
          className="bg-white/5 border-white/10 flex-1"
          data-testid="invite-target-input"
        />
        <Button onClick={create} disabled={creating} className="bg-fuchsia-600 hover:bg-fuchsia-500 shrink-0" data-testid="create-invite-btn">
          {creating ? "Creating..." : "Create invite link"}
        </Button>
      </div>

      {createdLink && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-fuchsia-500/10 border border-fuchsia-500/30 mb-4" data-testid="created-invite-link">
          <LinkIcon className="w-4 h-4 text-fuchsia-400 shrink-0" />
          <span className="text-sm font-mono truncate flex-1">{createdLink}</span>
          <button type="button" onClick={() => copy(createdLink)} className="p-1.5 text-muted-foreground hover:text-fuchsia-400" data-testid="copy-created-invite-btn">
            <Copy className="w-4 h-4" />
          </button>
        </div>
      )}

      {data.invites.length > 0 && (
        <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="invites-list">
          {data.invites.map((inv) => (
            <div key={inv.code} className="flex items-center justify-between gap-3 text-sm px-3 py-2 rounded-lg bg-white/5 flex-wrap" data-testid={`invite-row-${inv.code}`}>
              <div className="min-w-0">
                <span className="font-medium">{inv.style === "dream_date" ? "🌹" : "💌"} {inv.companion_name}</span>
                <span className="text-xs text-muted-foreground ml-2 font-mono">{inv.code}</span>
                {inv.target_note && <span className="text-xs text-muted-foreground ml-2 truncate">→ {inv.target_note}</span>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {inv.status === "claimed" && (
                  <>
                    <span className="text-xs text-muted-foreground" data-testid={`invite-msgs-${inv.code}`}>💬 {inv.messages_sent ?? 0}</span>
                    {inv.is_paying ? (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/15 text-amber-400 border border-amber-500/30" data-testid={`invite-paying-${inv.code}`}>💳 Paying ×{inv.purchases}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Free user</span>
                    )}
                  </>
                )}
                {statusChip(inv)}
                <button type="button" onClick={() => copy(inv.invite_link)} className="p-1.5 text-muted-foreground hover:text-fuchsia-400" title="Copy link" data-testid={`copy-invite-${inv.code}`}>
                  <LinkIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
